import { useCallback, useEffect, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import { publicReportAPI } from '@/lib/api/publicReports'
import type { ReportVersionView } from '@/lib/types/report'
import { useReportLive } from '@/hooks/report/useReportLive'
import { changedSectionIds } from '@/lib/report/versionDiff'
import { ReportViewer } from '@/components/report/ReportViewer'

/** Public, no-login share view (/r/:token). Polling only — no socket, no controls. */
export function PublicReportPage() {
  const { token } = useParams<{ token: string }>()
  const fetchView = useCallback(
    () => publicReportAPI.getReport(token as string).then((r) => r.data),
    [token],
  )
  const { view, error, isLive } = useReportLive({ fetchView, reportId: null })

  const [selected, setSelected] = useState<number | null>(null)
  const [snapshot, setSnapshot] = useState<ReportVersionView | null>(null)
  const prevHeadRef = useRef(0)
  const [changedIds, setChangedIds] = useState<string[]>([])

  useEffect(() => {
    if (!view) return
    if (prevHeadRef.current > 0 && view.version > prevHeadRef.current) {
      setChangedIds(changedSectionIds(view.versions, prevHeadRef.current, view.version))
    }
    prevHeadRef.current = view.version
  }, [view])

  const onSelectVersion = useCallback(
    (v: number) => {
      if (!view) return
      if (v === view.version) {
        setSelected(null)
        setSnapshot(null)
        return
      }
      setSelected(v)
      publicReportAPI.getVersion(token as string, v).then((res) => setSnapshot(res.data))
    },
    [token, view],
  )

  if (error) {
    return (
      <div className="mx-auto max-w-xl p-12 text-center">
        <p className="text-lg font-medium text-foreground">This share link is invalid or was revoked.</p>
        <p className="mt-2 text-sm text-muted-foreground">Ask the report owner for a new link.</p>
      </div>
    )
  }
  if (!view) return <div className="mx-auto max-w-3xl p-8 text-muted-foreground">Loading report…</div>

  const scrubbing = selected != null && snapshot != null
  return (
    <div className="min-h-screen bg-background px-4 py-8">
      <ReportViewer
        view={scrubbing ? snapshot : view}
        versions={view.versions}
        selected={scrubbing ? (selected as number) : view.version}
        headVersion={view.version}
        isLive={isLive && !scrubbing}
        changedIds={scrubbing ? [] : changedIds}
        contentVersion={scrubbing ? (selected as number) : view.version}
        onSelectVersion={onSelectVersion}
      />
      <p className="print:hidden mx-auto mt-8 max-w-4xl text-center text-xs text-muted-foreground">
        Shared read-only report · Powered by EcoSemantic
      </p>
    </div>
  )
}

export default PublicReportPage
